import { useEffect, useState } from "react";
import { cn } from "../cn";
import { fmtCountdown } from "./mkt-format";
import { useT } from "../../i18n";

/**
 * MARKET-TIME countdown. `marketNow` is the authoritative server MARKET TIME at
 * fetch; we tick locally from the measured skew (market time == real time while
 * the market is not paused). Fail-closed: once elapsed it shows "Ended".
 */
export function MarketClock({
  endMarketTs,
  marketNow,
  antiSnipeWindow = 0,
  testId = "market-clock",
}: {
  endMarketTs: number;
  marketNow: number;
  antiSnipeWindow?: number;
  testId?: string;
}) {
  const t = useT();
  const [skew] = useState(() => marketNow - Math.floor(Date.now() / 1000));
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000) + skew);

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000) + skew), 1000);
    return () => clearInterval(id);
  }, [skew]);

  const remaining = endMarketTs - now;
  const ended = remaining <= 0;
  const closing = !ended && antiSnipeWindow > 0 && remaining <= antiSnipeWindow;

  return (
    <span
      data-testid={testId}
      data-ended={ended ? "true" : "false"}
      className={cn(
        "inline-flex items-center gap-1.5 font-mono tabular-nums font-semibold tracking-[0.08em]",
        ended ? "text-text-muted" : closing ? "text-neon-coral" : "text-neon-teal",
      )}
    >
      {!ended && (
        <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", closing ? "bg-neon-coral" : "bg-neon-teal", "animate-pulse")} />
      )}
      {ended ? t("marketplace.clock.ended") : fmtCountdown(remaining)}
      {closing && <span className="ml-1 text-[9px] uppercase tracking-[0.14em]">{t("marketplace.clock.closing")}</span>}
    </span>
  );
}
